import { useEffect, useMemo } from "react";
import "./MasterTrainerCinematic.css";

/**
 * MasterTrainerCinematic — rarest cinematic in the app. Fires exactly
 * once per user, the moment they've defeated all 9 NPC trainers.
 *
 * Visual: full-screen golden bloom radiating from center, a slow ring
 * of all 9 trainer portraits orbits in, the title "MASTER TRAINER"
 * locks in with a starburst, then everything fades. ~5.5s total so it
 * feels significant without being interruptive.
 *
 * Props:
 *   show       bool   — render & start the animation when true
 *   portraits  str[]  — absolute URLs (≤ 9) for the orbit ring
 *   duration   ms     — total on-screen time (default 5500)
 *   onDone     fn     — fires after the cinematic completes
 */
export function MasterTrainerCinematic({
  show,
  portraits = [],
  duration = 5500,
  onDone,
}) {
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => onDone?.(), duration);
    return () => clearTimeout(t);
  }, [show, duration, onDone]);

  // Pre-compute orbit positions so the ring is symmetrical.
  const orbit = useMemo(() => {
    const n = Math.min(9, portraits.length || 9);
    return Array.from({ length: n }, (_, i) => {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      const radius = 170;
      return {
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius,
        delay: 0.45 + i * 0.08,
        portrait: portraits[i] || null,
      };
    });
  }, [portraits]);

  if (!show) return null;

  return (
    <div
      className="master-trainer"
      role="presentation"
      onClick={() => onDone?.()}
      data-testid="master-trainer-cinematic"
    >
      <div className="master-trainer__veil" />
      <div className="master-trainer__bloom" />
      <div className="master-trainer__rays" aria-hidden="true">
        {Array.from({ length: 12 }, (_, i) => (
          <span
            key={i}
            className="master-trainer__ray"
            style={{ "--angle": `${(i / 12) * 360}deg` }}
          />
        ))}
      </div>

      <div className="master-trainer__stage">
        {orbit.map((o, i) => (
          <div
            key={i}
            className="master-trainer__portrait"
            style={{
              "--tx": `${o.x}px`,
              "--ty": `${o.y}px`,
              "--delay": `${o.delay}s`,
            }}
          >
            {o.portrait ? (
              <img
                src={o.portrait}
                alt=""
                draggable={false}
                onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
              />
            ) : (
              <div className="master-trainer__portrait-fallback">★</div>
            )}
          </div>
        ))}
        <div className="master-trainer__center">
          <div className="master-trainer__star">★</div>
          <div className="master-trainer__title" data-testid="master-trainer-title">
            MASTER TRAINER
          </div>
          <div className="master-trainer__subtitle">
            All 9 Trainers Defeated
          </div>
        </div>
      </div>
    </div>
  );
}
